import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchOrgInvitations,
  inviteUserByPhone,
  revokeOrgInvitation,
  type OrgInvitation,
} from "@/lib/api";
import { inviteSmsReasonText, maskPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { Clock, Send, X } from "@/components/icons";

/**
 * Pending phone invitations for one organization.
 *
 * An invitation is recorded whether or not it can be texted (the SMS channel and
 * self-registration are separate kill switches), so this section lists what is
 * outstanding and, when the channel is off, surfaces the registration link for
 * the operator to pass on by hand.
 *
 * Authorization stays with the backend: every button calls an org-scoped endpoint
 * that re-checks the caller. `isAdmin` only decides what is worth rendering — a
 * plain org manager may not revoke a manager invitation (the server answers 403),
 * so that button is not shown to them.
 */
export function PendingInvitations({
  orgId,
  canManage,
  isAdmin,
}: {
  orgId: number;
  /** Whether the current user manages this org at all. */
  canManage: boolean;
  /** System admin — may act on manager-role invitations too. */
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();

  const invitesQ = useQuery({
    queryKey: ["org-invitations", orgId],
    queryFn: () => fetchOrgInvitations(orgId),
    enabled: canManage,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["org-invitations", orgId] });

  const revokeM = useMutation({
    mutationFn: (invitationId: number) => revokeOrgInvitation(orgId, invitationId),
    onSuccess: () => {
      toast.success("Invitation revoked");
      refresh();
    },
    onError: (e: any) => toast.error(e?.message || "Could not revoke the invitation"),
  });

  // Resend = re-POST the same phone + role. The backend reuses the existing
  // pending row (idempotent per org+phone) and texts it again, so this can never
  // create a duplicate invitation.
  const resendM = useMutation({
    mutationFn: (inv: OrgInvitation) => inviteUserByPhone(orgId, inv.phone, inv.role),
    onSuccess: (res) => {
      if (res.sms_sent) toast.success("Invitation re-sent by text message");
      else toast.warning(inviteSmsReasonText(res.sms_reason));
      refresh();
    },
    onError: (e: any) => toast.error(e?.message || "Could not re-send the invitation"),
  });

  if (!canManage) return null;

  const invitations = invitesQ.data?.invitations ?? [];
  const smsChannel = invitesQ.data?.sms_channel;
  const registrationLink = invitesQ.data?.registration_link;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm">Pending invitations ({invitations.length})</h4>
      </div>

      {/* An invitation that cannot be texted is not broken; it just needs the
          link delivered another way. */}
      {smsChannel && !smsChannel.available && (
        <div className="mb-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
          <div>{inviteSmsReasonText(smsChannel.reason)}</div>
          {registrationLink && (
            <div className="mt-1 opacity-70">
              Invitees can still register at <span className="font-mono">{registrationLink}</span>{" "}
              using the phone number you invited.
            </div>
          )}
        </div>
      )}

      {invitesQ.isLoading ? (
        <div className="text-sm opacity-50 italic">Loading…</div>
      ) : invitations.length === 0 ? (
        <div className="text-sm opacity-50 italic">No pending invitations</div>
      ) : (
        <div className="space-y-1">
          {invitations.map((inv) => {
            // Mirrors the server rule: only a system admin may act on a pending
            // manager invitation.
            const mayAct = isAdmin || inv.role === "driver";
            return (
              <div
                key={inv.id}
                className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-muted/50 text-sm"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Clock className="h-3.5 w-3.5 shrink-0 opacity-50" />
                  <span className="font-mono">{maskPhone(inv.phone)}</span>
                  <OrgRoleBadge role={inv.role} />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs opacity-60">{formatInvitedAt(inv)}</span>
                  {mayAct && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2"
                        title="Re-send the invitation text message"
                        disabled={resendM.isPending}
                        onClick={() => resendM.mutate(inv)}
                      >
                        <Send className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-destructive hover:text-destructive"
                        title="Revoke this invitation"
                        disabled={revokeM.isPending}
                        onClick={() => {
                          if (confirm(`Revoke the invitation for ${maskPhone(inv.phone)}?`))
                            revokeM.mutate(inv.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "Invited 12 Aug by admin" — the inviter falls back to their id, then to
 *  nothing, so a deleted account never blanks out the date too. */
function formatInvitedAt(inv: OrgInvitation): string {
  const when = inv.created_at
    ? new Date(inv.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
  const who =
    inv.invited_by_username ??
    (inv.invited_by_user_id != null ? `user ${inv.invited_by_user_id}` : null);
  if (when && who) return `Invited ${when} by ${who}`;
  if (when) return `Invited ${when}`;
  return who ? `Invited by ${who}` : "Invited";
}
